import { useState, useEffect } from "react";
import { Box, Grid, Select, MenuItem } from "@mui/material";
import { styled } from "@mui/material/styles";
import { tableCellClasses } from "@mui/material/TableCell";
import {
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tooltip,
  EditIcon,
  CloseIcon,
  Autocomplete,
  Button,
  TextField,
  Typography,
} from "../../mui";
import { FormContainer } from "react-hook-form-mui";
import UIWrapper from "../../components/UIWrapper";
import ClientNotificationService from "../../services/ClientNotificationService";
import { snackbarActions } from "../../Store/snackbar";
import { useDispatch } from "react-redux";
import AccService from "../../services/AccService";
import TextareaAutosize from "@mui/material/TextareaAutosize";
import ProjectSelect from "../Parents/ProjectSelect";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.selectedNavItemHover.bgColor,
    color: theme.palette.selectedNavItem.color,
  },
}));

const inquiryTableColumns = [
  "Client",
  "Project(s)",
  "Status",
  "Email(s)",
  "Action",
];

const ClientExternalNotification = () => {
  const [tableData, setTableData] = useState([]);
  const [rowsPerPageLarge, setRowsPerPageLarge] = useState(25);
  const [pageLarge, setPageLarge] = useState(0);
  const [client, setClient] = useState("");
  const [project, setProject] = useState([]);
  const [clientOptions, setClientOptions] = useState([]);
  const [projectOptions, setProjectOptions] = useState([]);
  const [status, setStatus] = useState("Active");
  const [emails, setEmails] = useState("");
  const [emailError, setEmailError] = useState("");
  const [id, setId] = useState("");
  const [deletePayload, setDeletePayload] = useState({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const dispatch = useDispatch();

  const handleChangePageLarge = (event, newPage) => {
    setPageLarge(newPage);
  };

  const handleChangeRowsPerPageLarge = (event) => {
    setRowsPerPageLarge(+event.target.value);
    setPageLarge(0);
  };

  const handleEditRowData = (row) => {
    setClient(row.client);
    // Ensure projects is always an array for ProjectSelect
    if (Array.isArray(row.projects)) {
      setProject(row.projects);
    } else if (typeof row.projects === "string" && row.projects.length > 0) {
      setProject(
        row.projects
          .split(",")
          .map((p) => p.trim())
          .filter((p) => p)
      );
    } else {
      setProject([]);
    }

    setEmails(row.email);
    setId(row.id);
    setStatus(row.is_active ? "Active" : "Inactive");
  };

  const commonDispatch = (type, message) => {
    dispatch(
      snackbarActions.showNotification({
        snackbarOpen: true,
        snackbarType: type,
        snackbarMessage: message,
      })
    );
  };

  const getProjectList = (clientName) => {
    if (clientName) {
      AccService.getClientProjects(clientName)
        .then((resp) => setProjectOptions(resp.data))
        .catch(() => setProjectOptions([]));
    }
  };

  useEffect(() => {
    getProjectList(client);
  }, [client]);

  useEffect(() => {
    AccService.getAllClients("").then((resp) => setClientOptions(resp.data));
    return;
  }, []);

  const getNotificationData = () => {
    ClientNotificationService.geNotificationList()
      .then((resp) => {
        setTableData(resp.data);
      })
      .catch((err) => {
        commonDispatch("error", err.message);
      });
  };

  useEffect(() => {
    getNotificationData();
  }, []);

  // Email validation function
  const validateEmails = (emailsStr) => {
    if (!emailsStr) return "";
    const emailArr = emailsStr
      .split(";")
      .map((email) => email.trim())
      .filter((email) => email);
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    for (let email of emailArr) {
      if (!emailRegex.test(email)) {
        return "Please enter valid email(s), separated by semicolons.";
      }
    }
    return "";
  };

  const handleEmailsChange = (e) => {
    setEmails(e.target.value);
    setEmailError(validateEmails(e.target.value));
  };

  const onSaveClickHandle = () => {
    const errorMsg = validateEmails(emails);
    setEmailError(errorMsg);
    if (errorMsg) return;

    const payload = {
      client: client,
      projects: project,
      email: emails,
      is_active: status === "Active" ? true : false,
    };

    // ...(id && { id: id }), // Add id if ID has value

    ClientNotificationService.createExternalNotification(payload)
      .then((resp) => {
        setClient("");
        setProject([]);
        setEmails("");
        commonDispatch("success", resp.data.message);
        getNotificationData();
      })
      .catch((err) => {
        commonDispatch("error", "err.message");
      });
  };

  const onEditClickHandle = () => {
    const errorMsg = validateEmails(emails);
    setEmailError(errorMsg);
    if (errorMsg) return;

    const payload = {
      client: client,
      projects: project,
      email: emails,
      is_active: status === "Active" ? true : false,
      id: id,
    };

    ClientNotificationService.editExternalNotification(payload)
      .then((resp) => {
        setClient("");
        setProject([]);
        setEmails("");
        setId("");
        commonDispatch("success", resp.data.message);
        getNotificationData();
      })
      .catch((err) => {
        commonDispatch("error", "err.message");
      });
  };

  const onDeleteClickHandle = () => {
    ClientNotificationService.deleteExternalNotification(deletePayload)
      .then((resp) => {
        setDeletePayload({});
        commonDispatch("success", resp.data.message);
        getNotificationData();
      })
      .catch((err) => {
        commonDispatch("error", "err.message");
      });
  };

  const handleDeleteClick = (row) => {
    const data = { client: row.client, id: row.id };
    setDeletePayload(data);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    setShowDeleteModal(false);
    onDeleteClickHandle();
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
  };

  return (
    <UIWrapper title="External Notification" paper>
      <Box>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ marginBottom: "15px" }}>
              Create External Notification
            </Typography>
            <FormContainer sx={{ width: "100%" }}>
              <Grid container spacing={2} xs={12}>
                <Grid item xs={2}>
                  <Autocomplete
                    id="free-solo-demo"
                    freeSolo
                    size="small"
                    options={clientOptions}
                    onChange={(e, new_value) => setClient(new_value)}
                    renderInput={(params) => (
                      <TextField {...params} label="Client" />
                    )}
                    value={client}
                  />
                </Grid>
                <Grid item xs={3}>
                  <ProjectSelect
                    projectOptions={projectOptions}
                    projectState={project}
                    setProjectState={setProject}
                    disabled={!client}
                    dashboardData={{ project: project }}
                    minWidth="100%"
                  />
                </Grid>
                <Grid item xs={2}>
                  <Select
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    displayEmpty
                    fullWidth
                    size="small"
                  >
                    <MenuItem value="Active">Active</MenuItem>
                    <MenuItem value="Inactive">Inactive</MenuItem>
                  </Select>
                </Grid>
                <Grid item xs={4}>
                  <TextareaAutosize
                    aria-label="Enter Email(s)"
                    minRows={2}
                    placeholder="Enter Email(s) - semicolon separated"
                    style={{ width: 300 }}
                    value={emails}
                    onChange={handleEmailsChange}
                  />
                  {emailError && (
                    <Typography
                      variant="caption"
                      color="error"
                      sx={{ display: "block", fontSize: "0.7rem" }}
                    >
                      {emailError}
                    </Typography>
                  )}
                </Grid>

                <Grid item xs={1}>
                  {!id ? (
                    <Button
                      variant="contained"
                      onClick={onSaveClickHandle}
                      disabled={!!emailError || !client || !project.length}
                    >
                      Save
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      onClick={onEditClickHandle}
                      disabled={!!emailError || !client || !project.length}
                    >
                      Edit
                    </Button>
                  )}
                </Grid>
              </Grid>
            </FormContainer>
          </Grid>
        </Grid>

        <Divider style={{ width: "100%", margin: "15px 0px" }}></Divider>

        {/*  Notification Table */}
        <Stack>
          <TableContainer>
            <Table aria-label="Shipment Inquiries Table" size={"small"}>
              <TableHead>
                <TableRow>
                  {inquiryTableColumns.map((row, index) => (
                    <StyledTableCell align="left" key={index}>
                      {row}
                    </StyledTableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody style={{ border: "none" }}>
                {tableData.length > 0 ? (
                  tableData
                    .slice(
                      pageLarge * rowsPerPageLarge,
                      pageLarge * rowsPerPageLarge + rowsPerPageLarge
                    )
                    .map((row, index) => (
                      <TableRow
                        key={index}
                        sx={{
                          "&:last-child td, &:last-child th": { border: 0 },
                        }}
                      >
                        <TableCell align="left">
                          {row.client || "None / Null"}
                        </TableCell>
                        <TableCell align="left">
                          {Array.isArray(row.projects) && row.projects.length
                            ? row.projects.join(", ")
                            : "None / Null"}
                        </TableCell>
                        <TableCell align="left">
                          {row.is_active
                            ? "Active"
                            : "Inactive" || "None / Null"}
                        </TableCell>
                        <TableCell align="left">
                          {row.email
                            ? row.email
                                .split(";")
                                .map((e) => e.trim())
                                .filter((e) => e)
                                .join("; ")
                            : "None / Null"}
                        </TableCell>
                        <TableCell align="left">
                          <Tooltip
                            title="Edit Detail"
                            style={{ cursor: "pointer" }}
                          >
                            <EditIcon
                              color={"primary"}
                              onClick={() => handleEditRowData(row)}
                              sx={{ marginRight: "15px" }}
                            ></EditIcon>
                          </Tooltip>
                          <Tooltip
                            title="Delete Notification"
                            style={{ cursor: "pointer" }}
                          >
                            <CloseIcon
                              color={"error"}
                              onClick={() => handleDeleteClick(row)}
                            ></CloseIcon>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                ) : (
                  <TableRow>
                    <TableCell
                      align="center"
                      colSpan={inquiryTableColumns.length + 2}
                    >
                      No Record Found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[6, 10, 25]}
              component="div"
              count={tableData.length}
              rowsPerPage={rowsPerPageLarge}
              page={pageLarge}
              onPageChange={handleChangePageLarge}
              onRowsPerPageChange={handleChangeRowsPerPageLarge}
            />
          </TableContainer>
        </Stack>

        {/* Delete Confirmation Modal */}
        <Dialog open={showDeleteModal} onClose={handleCancelDelete}>
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete this notification?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelDelete} variant="outlined">
              Cancel
            </Button>
            <Button
              onClick={handleConfirmDelete}
              variant="contained"
              color="error"
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </UIWrapper>
  );
};

export default ClientExternalNotification;
